import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { landingPath } from '@/lib/landing';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      // Check for Career Buddy return flow
      const returnTo = searchParams.get('returnTo');
      if (returnTo === 'careerBuddy') {
        navigate('/careers?buddyRestore=1');
        return;
      }
      const redirect = searchParams.get('redirect');
      if (redirect) { navigate(redirect); return; }
      navigate(landingPath(result.data));
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <span className="text-[26px] font-bold text-[#1F2937] font-['Outfit'] tracking-tight flex items-center" data-testid="login-logo">
                Careers<span className="inline-flex items-center ml-0.5"><span className="bg-[#F97D6A] text-white text-[16px] font-extrabold px-2 py-[3px] rounded-[5px] leading-none tracking-wide">POV</span></span>
              </span>
            </div>
            <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Welcome back</h1>
            <p className="text-sm text-[#4B5563] mt-1">Sign in to your CareersPOV account</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-6" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[#1F2937] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1.5 rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A]"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#1F2937] font-medium">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#F97D6A] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A] pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-sm text-[#4B5563] mt-6" data-testid="login-no-account-note">
            <Link to="/bring-careerspov" className="text-[#F97D6A] font-medium hover:underline" data-testid="bring-to-school-link">
              Bring CareersPOV to My School
            </Link>
          </p>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-[#9CA3AF] hover:text-[#4B5563] inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" />
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
